import { addDays } from 'date-fns/addDays';
import { format } from 'date-fns/format';
import { useId, useMemo } from 'react';
import type { Address } from '../../data/address';
import type { Order } from '../../data/order';
import { caseCount, orderTotalCost, unitCount } from '../../data/order';
import { casesOf, type OrderItem, totalCost, unitsOf } from '../../data/order-item';
import type { Store } from '../../data/store';
import type { Vendor } from '../../data/vendor';
import { fullCurrencyFormatter, preciseCurrencyFormatter } from '../../helpers/format-currency';

function AddressDisplay({ name, address }: { name: string; address: Address }) {
  return (
    <div className="mt-2 mb-4 pl-2">
      <Line>{name} </Line>
      <Line>{address.line_one}</Line>
      <Line>{address.line_two}</Line>
      <Line>
        {address.city}, {address.state} {address.zipcode}
      </Line>
    </div>
  );
}

type LineProperties = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

function Line({ children, ...rest }: LineProperties) {
  if (!children) {
    return;
  }

  return <div {...rest}>{children}</div>;
}

type PurchaseOrderOptions = {
  store: Store;
  vendor: Vendor;
  order: Order;
  orderItems: OrderItem[];
};

export function PurchaseOrder({ orderItems, store, vendor, order }: PurchaseOrderOptions) {
  const headerId = useId();
  const contentId = useId();
  const total = useMemo(() => {
    return fullCurrencyFormatter(orderTotalCost(orderItems));
  }, [orderItems]);
  const totalCases = useMemo(() => caseCount(orderItems), [orderItems]);
  const totalUnits = useMemo(() => unitCount(orderItems), [orderItems]);

  return (
    <div className="text-xs">
      <h1 className="text-center uppercase font-semibold text-xl p-4 hidden print:block">
        Purchase Order
      </h1>

      <div className="grid grid-cols-3 p-4 pb-0">
        <div>
          {/* <!-- Column 1 --> */}
          <h2 className="pl-2 uppercase font-semibold bg-primary-700 text-white">Bill To</h2>
          <AddressDisplay name={store.name} address={store.billing_address} />
          <h2 className="pl-2 uppercase font-semibold bg-primary-700 text-white">Vendor</h2>
          <AddressDisplay name={vendor.name} address={vendor.address} />
        </div>
        <div>{/* <!-- Column 2 --> */}</div>
        <div>
          {/* <!-- Column 3 --> */}
          <h2 className="pl-2 uppercase font-semibold bg-primary-700 text-white">Order Date</h2>
          <div className="mt-2 mb-4 pr-2">
            <p className="text-right">{format(order.created_at, 'MM/dd/yyyy')}</p>
          </div>
          <h2 className="pl-2 uppercase font-semibold bg-primary-700 text-white">Delivery Date</h2>
          <div className="mt-2 mb-4 pr-2">
            <p className="text-right">
              {format(addDays(order.submitted_at || order.created_at, 1), 'MM/dd/yyyy')}
            </p>
          </div>
          <h2 className="pl-2 uppercase font-semibold bg-primary-700 text-white">Ship To</h2>
          <div className="text-right pr-2">
            <AddressDisplay name={store.name} address={store.billing_address} />
          </div>
        </div>
      </div>
      <div
        id={headerId}
        className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr] p-4 pb-0 divide-x border-primary-200"
      >
        <div className="border-b">
          <h2 className="pl-2 uppercase font-semibold bg-primary-700 text-white grid place-content-center">
            Account / License #
          </h2>
          <div className="grid place-content-center">
            <p>{vendor.account_number}</p>
          </div>
        </div>
        <div className="border-b">
          <h2 className="pl-2 uppercase font-semibold bg-primary-700 text-white grid place-content-center">
            Terms
          </h2>
          <div className="grid place-content-center">
            <p>{vendor.terms}</p>
          </div>
        </div>
        <div className="border-b">
          <h2 className="pl-2 uppercase font-semibold bg-primary-700 text-white grid place-content-center">
            Cases
          </h2>
          <div className="grid place-content-center">
            <p>{totalCases}</p>
          </div>
        </div>
        <div className="border-b">
          <h2 className="pl-2 uppercase font-semibold bg-primary-700 text-white grid place-content-center">
            Units
          </h2>
          <div className="grid place-content-center">
            <p>{totalUnits}</p>
          </div>
        </div>
        <div className="border-b">
          <h2 className="pl-2 uppercase font-semibold bg-primary-700 text-white grid place-content-center">
            Total
          </h2>
          <p className="pr-2 grid place-content-end">{total}</p>
        </div>
      </div>
      <div id={contentId} className="p-4">
        <table className="table-auto w-full border-collapse">
          <thead className="font-semibold bg-primary-700 text-white uppercase text-center">
            <tr>
              <th className="px-1 border-b border-primary-600" colSpan={10}>
                {store.name} –{' '}
                {format(order.submitted_at || order.created_at, 'MM/dd/yyyy hh:mmaa')} – ID #{' '}
                {order.id}
              </th>
            </tr>
            <tr>
              <th className="px-1 border-r border-primary-600" rowSpan={2}>
                #
              </th>
              <th className="px-1 border-x border-primary-600 max-w-36 text-clip" rowSpan={2}>
                Brand
              </th>
              <th className="px-1 border-x border-primary-600" rowSpan={2}>
                Description
              </th>
              <th className="px-1 border-x border-primary-600" rowSpan={2}>
                Size
              </th>
              <th className="px-1 border-x border-primary-600" rowSpan={2}>
                Item #
              </th>
              <th className="px-1 border-x border-b border-primary-600" colSpan={2}>
                Quantity
              </th>
              <th className="px-1 border-x border-b border-primary-600" colSpan={2}>
                Cost
              </th>
              <th className="px-1 border-l border-primary-600" rowSpan={2}>
                Extended
              </th>
            </tr>
            <tr>
              <th className="px-1 border-x border-primary-600">Case</th>
              <th className="px-1 border-x border-primary-600">Unit</th>
              <th className="px-1 border-x border-primary-600">Case</th>
              <th className="px-1 border-x border-primary-600">Unit</th>
            </tr>
          </thead>
          <tbody>
            {orderItems.map((orderItem, index) => (
              <tr key={orderItem.product.id} className={index % 2 === 1 ? 'bg-slate-100' : ''}>
                <td className="px-1 border-r border-primary-600">{index + 1}</td>
                <td className="px-1 border-x border-primary-600 max-w-36 text-clip">
                  {orderItem.product.brand}
                </td>
                <td className="px-1 border-x border-primary-600">
                  {orderItem.product.description}
                </td>
                <td className="px-1 border-x border-primary-600">{orderItem.product.size}</td>
                <td className="px-1 border-x border-primary-600">{orderItem.product.item_code}</td>
                <td className="px-1 border-x border-primary-600 text-right">
                  {casesOf(orderItem) > 0 ? casesOf(orderItem) : ''}
                </td>
                <td className="px-1 border-x border-primary-600 text-right">
                  {unitsOf(orderItem) > 0 ? unitsOf(orderItem) : ''}
                </td>
                <td className="px-1 border-x border-primary-600 text-right">
                  {preciseCurrencyFormatter(
                    orderItem.product.case_qty * orderItem.product.last_cost
                  )}
                </td>
                <td className="px-1 border-x border-primary-600 text-right">
                  {preciseCurrencyFormatter(orderItem.product.last_cost)}
                </td>
                <td className="px-1 border-l border-primary-600 text-right">
                  {fullCurrencyFormatter(totalCost(orderItem))}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="font-semibold uppercase">
            <tr>
              <td className="px-1 border-r border-t-2 border-primary-600" colSpan={5}>
                Totals
              </td>
              <td className="px-1 border-x border-t-2 border-primary-600 text-right">
                {totalCases}
              </td>
              <td className="px-1 border-x border-t-2 border-primary-600 text-right">
                {totalUnits}
              </td>
              <td className="px-1 border-x border-t-2 border-primary-600" colSpan={2} />
              <td className="px-1 border-l border-t-2 border-primary-600 text-right">{total}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
